import React, { lazy, Suspense } from "react";
import { Redirect, Route } from "react-router-dom";
import { IonApp, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, IonSpinner, IonContent } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { useTranslation } from "react-i18next";
import { useStudent } from "../contexts/StudentContext";
import { useStudyTimer } from "../hooks/useStudyTimer";
import { useNotificationSync } from "../hooks/useNotificationSync";
import { useIsDesktop } from "../hooks/useBreakpoint";
import Sidebar from "./Sidebar";

import Dashboard from "../pages/Dashboard";
import Subjects from "../pages/Subjects";
import Progress from "../pages/Progress";
import Profile from "../pages/Profile";

// Écrans secondaires en lazy loading (bundle initial plus léger, comme GameTime)
const Chapters = lazy(() => import("../pages/Chapters"));
const ChapterPath = lazy(() => import("../pages/ChapterPath"));
const Theory = lazy(() => import("../pages/Theory"));
const Lesson = lazy(() => import("../pages/Lesson"));
const LearningProfileSetup = lazy(() => import("./LearningProfileSetup"));
const Chat = lazy(() => import("../pages/Chat"));
const Exercises = lazy(() => import("../pages/Exercises"));
const Test = lazy(() => import("../pages/Test"));
const TestResult = lazy(() => import("../pages/TestResult"));
const Review = lazy(() => import("../pages/Review"));
const NotificationSettings = lazy(() => import("../pages/NotificationSettings"));
const Premium = lazy(() => import("../pages/Premium"));
const Badges = lazy(() => import("../pages/Badges"));
const EditProfile = lazy(() => import("../pages/EditProfile"));
const Account = lazy(() => import("../pages/Account"));
const ParentAccess = lazy(() => import("../pages/ParentAccess"));
const ParentDashboard = lazy(() => import("../pages/ParentDashboard"));
const ParentLink = lazy(() => import("../pages/ParentLink"));

const Fallback: React.FC = () => (
  <IonContent className="ion-padding">
    <div className="flex items-center justify-center min-h-full">
      <IonSpinner name="crescent" color="primary" />
    </div>
  </IonContent>
);

const L: React.FC = ({ children }) => <Suspense fallback={<Fallback />}>{children}</Suspense>;

const TabLabel: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div className="flex flex-col items-center justify-center gap-1 py-1">
    <i className={`fa-solid ${icon} text-xl`} />
    <span className="text-[10px] font-semibold">{label}</span>
  </div>
);

/**
 * Routeur interne Ionic (pile de navigation). Même structure que GameTime/Home.jsx.
 * Mobile : barre d'onglets en bas. Grand écran (≥ 900 px) : barre latérale, onglets masqués.
 * Compte parent (profile.role === "parent") : onglets « Mes enfants » + « Profil » ; toute
 * route élève est redirigée vers /home/parent (et inversement pour un élève).
 */
const Home: React.FC = () => {
  const { t } = useTranslation();
  const { user, isParent } = useStudent();
  const isDesktop = useIsDesktop();
  useStudyTimer(user?.uid, !isParent);
  // Rappels locaux (natif) : replanifiés une fois le profil chargé et après chaque activité.
  useNotificationSync();

  return (
    <IonApp>
      <IonReactRouter>
        <div className={`app-shell ${isDesktop ? "desktop" : "mobile"}`}>
          {isDesktop && <Sidebar />}
          <div className="app-main">
            {isParent ? (
              <IonTabs>
                <IonRouterOutlet>
                  <Route exact path="/home/parent">
                    <L>
                      <ParentDashboard />
                    </L>
                  </Route>
                  <Route exact path="/home/parent/link">
                    <L>
                      <ParentLink />
                    </L>
                  </Route>
                  <Route exact path="/home/profile">
                    <Profile />
                  </Route>
                  <Route exact path="/home/edit-profile">
                    <L>
                      <EditProfile />
                    </L>
                  </Route>
                  <Route exact path="/home/account">
                    <L>
                      <Account />
                    </L>
                  </Route>
                  {/* Redirections : TOUJOURS des routes exactes dans un IonRouterOutlet — une route
                      non exacte « /home » capture toutes les URL /home/* et vide le contenu. */}
                  {["/home", "/home/dashboard", "/home/subjects", "/home/progress", "/home/notifications", "/home/parent-access", "/home/learning-profile"].map((p) => (
                    <Route key={p} exact path={p}>
                      <Redirect to="/home/parent" />
                    </Route>
                  ))}
                </IonRouterOutlet>

                <IonTabBar slot="bottom" className={isDesktop ? "tabbar-hidden" : ""}>
                  <IonTabButton tab="children" href="/home/parent">
                    <TabLabel icon="fa-children" label={t("tabs.children")} />
                  </IonTabButton>
                  <IonTabButton tab="profile" href="/home/profile">
                    <TabLabel icon="fa-user" label={t("tabs.profile")} />
                  </IonTabButton>
                </IonTabBar>
              </IonTabs>
            ) : (
              <IonTabs>
                <IonRouterOutlet>
                  <Route exact path="/home/dashboard">
                    <Dashboard />
                  </Route>
                  <Route exact path="/home/subjects">
                    <Subjects />
                  </Route>
                  <Route exact path="/home/progress">
                    <Progress />
                  </Route>
                  <Route exact path="/home/profile">
                    <Profile />
                  </Route>
                  <Route exact path="/home/edit-profile">
                    <L>
                      <EditProfile />
                    </L>
                  </Route>
                  <Route exact path="/home/account">
                    <L>
                      <Account />
                    </L>
                  </Route>

                  <Route exact path="/home/subject/:subjectId">
                    <L>
                      <Chapters />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId">
                    <L>
                      <ChapterPath />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/theory">
                    <L>
                      <Theory />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/lesson">
                    <L>
                      <Lesson />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/chat">
                    <L>
                      <Chat />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/exercises">
                    <L>
                      <Exercises />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/test">
                    <L>
                      <Test />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/result/:testId">
                    <L>
                      <TestResult />
                    </L>
                  </Route>
                  <Route exact path="/home/chapter/:chapterId/review">
                    <L>
                      <Review />
                    </L>
                  </Route>
                  <Route exact path="/home/notifications">
                    <L>
                      <NotificationSettings />
                    </L>
                  </Route>
                  <Route exact path="/home/learning-profile">
                    <L>
                      <LearningProfileSetup mode="edit" />
                    </L>
                  </Route>
                  <Route exact path="/home/premium">
                    <L>
                      <Premium />
                    </L>
                  </Route>
                  <Route exact path="/home/badges">
                    <L>
                      <Badges />
                    </L>
                  </Route>
                  <Route exact path="/home/parent-access">
                    <L>
                      <ParentAccess />
                    </L>
                  </Route>
                  {/* Redirections : TOUJOURS des routes exactes dans un IonRouterOutlet (voir plus haut). */}
                  {["/home", "/home/parent", "/home/parent/link"].map((p) => (
                    <Route key={p} exact path={p}>
                      <Redirect to="/home/dashboard" />
                    </Route>
                  ))}
                </IonRouterOutlet>

                <IonTabBar slot="bottom" className={isDesktop ? "tabbar-hidden" : ""}>
                  <IonTabButton tab="dashboard" href="/home/dashboard">
                    <TabLabel icon="fa-house" label={t("tabs.home")} />
                  </IonTabButton>
                  <IonTabButton tab="subjects" href="/home/subjects">
                    <TabLabel icon="fa-book-open" label={t("tabs.subjects")} />
                  </IonTabButton>
                  <IonTabButton tab="progress" href="/home/progress">
                    <TabLabel icon="fa-chart-line" label={t("tabs.progress")} />
                  </IonTabButton>
                  <IonTabButton tab="profile" href="/home/profile">
                    <TabLabel icon="fa-user" label={t("tabs.profile")} />
                  </IonTabButton>
                </IonTabBar>
              </IonTabs>
            )}
          </div>
        </div>
      </IonReactRouter>
    </IonApp>
  );
};

export default Home;
